import { IconChevronChevronLeft } from '@doltech/icons/IconChevronChevronLeft';
import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { compose } from '@doltech/utils/lib/compose';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { responsiveBreakPoints } from '../../../hooks/useDeviceQuery.hook';
import { colorsV2 } from '../../colors-v2';
import { Swiper, SwiperSlide } from '../../DolSwiper/DolSwiper';
import { shadows } from '../../shadows';
import { useSingleCardSwiperStore, withSingleCardSwiperStore } from './single-card-swiper.store';

const Main = styled.div`
  width: 100%;
  .header-title {
    margin-bottom: 0;
  }
  .swiper-container {
    position: relative;
    &.with-pagination {
      padding-bottom: 26px;
    }
  }
  .swiper-pagination {
    bottom: 0;
  }
  .swiper-pagination-bullet {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    will-change: width;
    transition: all 0.3s ease;
    background: ${colorsV2.black100};
    &.swiper-pagination-bullet-active {
      width: 16px;
      border-radius: 3px;
    }
  }

  .swiper-navigation {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    line-height: 0;
    border: 1px solid ${colorsV2.black100};
    color: ${colorsV2.black100};
    font-size: 24px;
    transition: all 0.3s ease;
    cursor: pointer;
    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    &:hover {
      transform: scale(1.1);
    }
    &:active {
      transform: scale(0.9);
    }
  }

  .header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    margin-bottom: 16px;
  }
  .header-actions {
    display: none;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    gap: 16px;
  }

  @media ${responsiveBreakPoints['above-528']} {
    .header-actions {
      display: grid;
    }
  }
`;

export const SingleCardSlide = styled.div`
  &.styled {
    background: ${colorsV2.gray5};
    border: 1px solid ${colorsV2.black10};
    box-shadow: ${shadows.zIndex4};
    border-radius: 8px;
    padding: 16px;
  }
`;

const StyledSwiper = styled<any>(Swiper)``;

interface SingleCardSwiperProps {
  id: string;
  title: string;
  dataSource: any;
  renderItem: (item: any, index: number) => JSX.Element;
  useDefaultCardStyled?: boolean;

  showPagination?: boolean;
  renderHeader?: ({ id, dataSource }) => JSX.Element;
  swiperProps?: any;
  customWidth?: (width) => any[];
}

const SingleCardSwiperBase = (props: SingleCardSwiperProps) => {
  const {
    id,
    title,
    dataSource,
    renderItem,
    useDefaultCardStyled = true,
    showPagination = true,
    renderHeader,
    swiperProps = {},
    customWidth,
  } = props;
  const swiperRef = React.useRef<any>();
  const store = useSingleCardSwiperStore();
  const [widthOfBlogEditor, setWidthOfBlogEditor] = React.useState(263);

  React.useEffect(() => {
    if (swiperRef.current) {
      store.registerSwiper(swiperRef.current);
    }
    const el = document.querySelector('.blog-editor-container .dol-editor');
    if (el) {
      setWidthOfBlogEditor(el?.clientWidth);
    }
  }, [store]);

  React.useEffect(() => {
    store.setSlideCount(dataSource?.length || 0);
  }, [dataSource?.length, store]);

  return (
    <Main className="single-card-swiper">
      {renderHeader ? (
        renderHeader({ id, dataSource })
      ) : (
        <div className="header">
          <h2 className="header-title">{title}</h2>
          <div className="header-actions">
            <div className={cl('swiper-navigation prev', id)}>
              <IconChevronChevronLeft />
            </div>
            <div className={cl('swiper-navigation next', id)}>
              <IconChevronChevronRight />
            </div>
          </div>
        </div>
      )}
      <StyledSwiper
        ref={swiperRef as any}
        onSlideChange={({ realIndex }) => {
          store.setSlideIndex(realIndex);
        }}
        shortSwipes={false}
        gap={24}
        pagination={{
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        }}
        navigation={{
          nextEl: `.swiper-navigation.next.${id}`,
          prevEl: `.swiper-navigation.prev.${id}`,
          disabledClass: 'disabled',
        }}
        disableFreeMode
        className={cl('swiper-container', { 'with-pagination': showPagination })}
        {...swiperProps}
      >
        {dataSource.map((data, index) => {
          return (
            <SwiperSlide
              $breakpoints={[0, 540, 1024]}
              $width={customWidth ? customWidth(widthOfBlogEditor) : [
                widthOfBlogEditor ? widthOfBlogEditor - 96 : 'calc(100vw - 96px)',
                widthOfBlogEditor ? widthOfBlogEditor - 96 : 478,
                widthOfBlogEditor ? widthOfBlogEditor - 96 : 478,
              ]}
              key={data.id || index}
            >
              <SingleCardSlide className={cl({ styled: useDefaultCardStyled })}>
                {renderItem(data, index)}
              </SingleCardSlide>
            </SwiperSlide>
          );
        })}
        {showPagination && <div className="swiper-pagination" />}
      </StyledSwiper>
    </Main>
  );
};

export const SingleCardSwiper = compose(withSingleCardSwiperStore, observer)(SingleCardSwiperBase);
